/**
 * Realistic Element Renderer for Standard Template Preview
 * Renders template elements with realistic dummy data instead of placeholders
 */

import type { TemplateElement } from "@/features/invoice-template/types/template.types";
import { useTemplateContext } from "./template-context";
import { useCurrencyFormat } from "@/hooks/use-currency-format";
interface RealisticElementRendererProps {
  element: TemplateElement;
  globalFontFamily: string;
  globalFontSize: number;
  globalFontColor: string;
}

// Dummy data for realistic preview
const DUMMY_DATA = {
  company: {
    name: "Musterfirma GmbH",
    street: "Musterstraße",
    houseNumber: "123",
    zip: "12345",
    city: "Musterstadt",
    country: "Deutschland",
    email: "info@musterfirma.de",
    phone: "+49 123 456789",
    website: "www.musterfirma.de",
    vatId: "DE123456789",
    taxNumber: "123/456/78901",
    iban: "DE89 3704 0044 0532 0130 00",
    bic: "COBADEFFXXX",
    bankName: "Commerzbank",
    court: "Amtsgericht Musterstadt",
    hrn: "HRB 12345",
    contactFirstName: "Anna",
    contactLastName: "Schmidt",
    contactEmail: "a.schmidt@musterfirma.de",
  },
  customer: {
    name: "Max Mustermann",
    street: "Beispielweg 456",
    zip: "54321",
    city: "Beispielstadt",
  },
  invoice: {
    number: "RE-2024-001",
    date: "28.12.2024",
    dueDate: "11.01.2025",
    orderNumber: "BST-2024-042",
    customerNumber: "KD-10042",
  },
};

export function RealisticElementRenderer({
  element,
  globalFontFamily,
  globalFontSize,
  globalFontColor,
}: RealisticElementRendererProps) {
  const { tableColumns, companyDetails, logoUrl, logoWidth, logoHeight, accentColor } = useTemplateContext();
  const { formatCurrency } = useCurrencyFormat();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const el = element as any; // Type assertion for flexibility with dynamic element properties
  
  // Merge real company data with dummy data
  const actualCompanyData = {
    name: companyDetails?.name || DUMMY_DATA.company.name,
    street: companyDetails?.street || DUMMY_DATA.company.street,
    houseNumber: companyDetails?.houseNumber || DUMMY_DATA.company.houseNumber,
    zip: companyDetails?.zip || DUMMY_DATA.company.zip,
    city: companyDetails?.city || DUMMY_DATA.company.city,
    country: companyDetails?.country || DUMMY_DATA.company.country,
    email: companyDetails?.email || DUMMY_DATA.company.email,
    phone: companyDetails?.phone || DUMMY_DATA.company.phone,
    website: companyDetails?.website || DUMMY_DATA.company.website,
    vatId: companyDetails?.vatId || DUMMY_DATA.company.vatId,
    taxNumber: companyDetails?.taxNumber || DUMMY_DATA.company.taxNumber,
    iban: companyDetails?.iban || DUMMY_DATA.company.iban,
    bic: companyDetails?.bic || DUMMY_DATA.company.bic,
    bankName: companyDetails?.bankName || DUMMY_DATA.company.bankName,
    court: companyDetails?.court || DUMMY_DATA.company.court,
    hrn: companyDetails?.hrn || DUMMY_DATA.company.hrn,
    contactFirstName: companyDetails?.contactFirstName || DUMMY_DATA.company.contactFirstName,
    contactLastName: companyDetails?.contactLastName || DUMMY_DATA.company.contactLastName,
    contactEmail: companyDetails?.contactEmail || DUMMY_DATA.company.contactEmail,
  };
  
  // Check if this is RECHNUNG header to apply accent color
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const content = (element as any).content || "";
  const isInvoiceHeader = content === "RECHNUNG";
  const textColor = isInvoiceHeader && accentColor ? accentColor : (el.fontColor || globalFontColor);
  
  const style: React.CSSProperties = {
    fontFamily: el.fontFamily || globalFontFamily,
    fontSize: el.fontSize || globalFontSize,
    color: textColor,
    fontWeight: el.fontWeight,
    fontStyle: el.fontStyle,
    textDecoration: el.textDecoration,
    textAlign: el.textAlign,
    lineHeight: el.lineHeight,
    letterSpacing: el.letterSpacing ? `${el.letterSpacing}px` : undefined,
    backgroundColor: el.backgroundColor,
    padding: el.padding,
    margin: el.margin,
  };

  // Render based on element type
  const renderContent = () => {
    const elementType = element.type as string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const el = element as any;

    switch (elementType) {
      case "text": {
        let content = el.content || "";
        
        // Replace placeholders with dummy data
        const replacements: Record<string, string> = {
          '{Rechnungsnummer}': DUMMY_DATA.invoice.number,
          '{Rechnungsdatum}': DUMMY_DATA.invoice.date,
          '{Zahlungsziel}': DUMMY_DATA.invoice.dueDate,
          '{Bestellnummer}': DUMMY_DATA.invoice.orderNumber,
          '{Kundennummer}': DUMMY_DATA.invoice.customerNumber,
          '{Leitweg-ID}': '991-12345-67',
          '{Gesamtbetrag}': '2.677,50 €',
          '{Firmenname}': actualCompanyData.name,
          '{Strasse}': actualCompanyData.street,
          '{Hausnummer}': actualCompanyData.houseNumber,
          '{PLZ}': actualCompanyData.zip,
          '{Ort}': actualCompanyData.city,
          '{Land}': actualCompanyData.country,
          '{Telefon}': actualCompanyData.phone,
          '{E-Mail}': actualCompanyData.email,
          '{Website}': actualCompanyData.website,
          '{IBAN}': actualCompanyData.iban,
          '{SWIFT/BIC}': actualCompanyData.bic,
          '{Bank}': actualCompanyData.bankName,
          '{Amtsgericht}': actualCompanyData.court,
          '{HR-Nr}': actualCompanyData.hrn,
          '{Steuernummer}': actualCompanyData.taxNumber,
          '{Ust-IdNr}': actualCompanyData.vatId,
          '{Ansprechpartner-Vorname}': actualCompanyData.contactFirstName,
          '{Ansprechpartner-Nachname}': actualCompanyData.contactLastName,
          '{Ansprechpartner-EMail}': actualCompanyData.contactEmail,
        };
        
        // Replace all placeholders
        Object.entries(replacements).forEach(([placeholder, value]) => {
          content = content.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
        });
        
        // Split by newlines and render with <br> tags
        const lines = content.split('\n');
        return (
          <>
            {lines.map((line: string, index: number) => (
              <span key={index}>
                {line}
                {index < lines.length - 1 && <br />}
              </span>
            ))}
          </>
        );
      }

      case "invoice-number":
        return DUMMY_DATA.invoice.number;

      case "invoice-date":
        return DUMMY_DATA.invoice.date;

      case "payment-due-date": {
        const format = el.paymentDueDateFormat;
        return format === "text" ? "Zahlbar innerhalb von 14 Tagen" : DUMMY_DATA.invoice.dueDate;
      }

      case "order-number":
        return DUMMY_DATA.invoice.orderNumber;

      case "customer-number":
        return DUMMY_DATA.invoice.customerNumber;

      case "leitweg-id":
        return "991-12345-67";

      case "vat-id":
        return actualCompanyData.vatId;

      case "tax-number":
        return actualCompanyData.taxNumber;

      case "page-number": {
        const pageFormat = el.pageNumberFormat || "page-x";
        switch (pageFormat) {
          case "page-x":
            return "Seite 1";
          case "page-x-of-y":
            return "Seite 1 von 2";
          case "x":
            return "1";
          case "x-slash-y":
            return "1/2";
          case "x-dash-y":
            return "1-2";
          default:
            return "Seite 1";
        }
      }

      case "sender": {
        const senderMode = el.displayMode || "block";
        if (senderMode === "inline") {
          return (
            <div style={{ fontSize: "0.7em", opacity: 0.7 }}>
              {actualCompanyData.name} - {actualCompanyData.street} {actualCompanyData.houseNumber} - {actualCompanyData.zip} {actualCompanyData.city}
            </div>
          );
        }
        // Mehrzeilige Block-Darstellung
        return (
          <>
            <div>{actualCompanyData.name}</div>
            <div>{actualCompanyData.street} {actualCompanyData.houseNumber}</div>
            <div>
              {actualCompanyData.zip} {actualCompanyData.city}
            </div>
          </>
        );
      }

      case "recipient":
        return (
          <>
            <div>{DUMMY_DATA.customer.name}</div>
            <div>{DUMMY_DATA.customer.street}</div>
            <div>
              {DUMMY_DATA.customer.zip} {DUMMY_DATA.customer.city}
            </div>
          </>
        );

      case "company-name":
        return actualCompanyData.name;

      case "company-address": {
        const addressMode = el.displayMode || "block";
        if (addressMode === "inline") {
          return `${actualCompanyData.street} ${actualCompanyData.houseNumber}, ${actualCompanyData.zip} ${actualCompanyData.city}`;
        }
        return (
          <>
            <div>{actualCompanyData.street} {actualCompanyData.houseNumber}</div>
            <div>
              {actualCompanyData.zip} {actualCompanyData.city}
            </div>
          </>
        );
      }

      case "company-email":
        return actualCompanyData.email;

      case "company-phone":
        return actualCompanyData.phone;

      case "bank-details": {
        const bankMode = el.displayMode || "block";
        if (bankMode === "inline") {
          return `IBAN: ${actualCompanyData.iban} • BIC: ${actualCompanyData.bic}`;
        }
        return (
          <>
            <div>IBAN: {actualCompanyData.iban}</div>
            <div>BIC: {actualCompanyData.bic}</div>
          </>
        );
      }

      case "bank-name":
        return actualCompanyData.bankName;

      case "iban":
        return actualCompanyData.iban;

      case "swift":
        return actualCompanyData.bic;

      case "email":
        return actualCompanyData.email;

      case "website":
        return actualCompanyData.website;

      case "country":
        return actualCompanyData.country;

      case "court":
        return actualCompanyData.court;

      case "hrn":
        return actualCompanyData.hrn;

      case "small-business-note":
        // Don't render in designer - only shown in actual PDF when applicable
        return null;

      case "message":
        return el.content || "";

      case "image": {
        // Use logoUrl from context with priority, fallback to element's imageUrl
        const imageUrl = logoUrl || el.imageUrl;
        console.log('Image element:', { contextLogoUrl: logoUrl, elImageUrl: el.imageUrl, finalImageUrl: imageUrl });
        if (imageUrl) {
          return (
            <img
              src={imageUrl}
              alt="Logo"
              style={{
                width: logoWidth ? `${logoWidth}px` : undefined,
                height: logoHeight ? `${logoHeight}px` : undefined,
                maxWidth: "100%",
                objectFit: "fill",
              }}
            />
          );
        }
        return null;
      }

      case "qr-code":
      case "qrcode":
        // Show placeholder for QR code
        return (
          <div
            style={{
              width: "100px",
              height: "100px",
              border: "2px solid #ccc",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "10px",
              color: "#999",
            }}
          >
            QR-Code
          </div>
        );

      case "invoice-table":
      case "table": {
        // Show comprehensive table preview with totals based on tableColumns config
        const cols = tableColumns || {
          position: true,
          description: true,
          quantity: true,
          unit: true,
          unitPrice: true,
          total: true,
          productType: false,
          itemDate: false,
        };
        
        const tableAccentColor = accentColor || "#000000";
        
        return (
          <div style={{ width: "100%" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${tableAccentColor}`, backgroundColor: `${tableAccentColor}15` }}>
                  {cols.position && <th style={{ textAlign: "left", padding: "8px" }}>Pos.</th>}
                  {cols.description && <th style={{ textAlign: "left", padding: "8px" }}>Beschreibung</th>}
                  {cols.productType && <th style={{ textAlign: "left", padding: "8px" }}>Typ</th>}
                  {cols.itemDate && <th style={{ textAlign: "left", padding: "8px" }}>Datum</th>}
                  {cols.quantity && <th style={{ textAlign: "right", padding: "8px" }}>Menge</th>}
                  {cols.unit && <th style={{ textAlign: "right", padding: "8px" }}>Einheit</th>}
                  {cols.unitPrice && <th style={{ textAlign: "right", padding: "8px" }}>Preis</th>}
                  {cols.total && <th style={{ textAlign: "right", padding: "8px" }}>Gesamt</th>}
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: `1px solid ${tableAccentColor}40` }}>
                  {cols.position && <td style={{ padding: "8px" }}>1</td>}
                  {cols.description && <td style={{ padding: "8px" }}>Beispielprodukt 1</td>}
                  {cols.productType && <td style={{ padding: "8px" }}>Dienstleistung</td>}
                  {cols.itemDate && <td style={{ padding: "8px" }}>28.12.2024</td>}
                  {cols.quantity && <td style={{ textAlign: "right", padding: "8px" }}>2</td>}
                  {cols.unit && <td style={{ textAlign: "right", padding: "8px" }}>Std</td>}
                  {cols.unitPrice && <td style={{ textAlign: "right", padding: "8px" }}>{formatCurrency(50)}</td>}
                  {cols.total && <td style={{ textAlign: "right", padding: "8px" }}>{formatCurrency(100)}</td>}
                </tr>
                <tr style={{ borderBottom: `1px solid ${tableAccentColor}40` }}>
                  {cols.position && <td style={{ padding: "8px" }}>2</td>}
                  {cols.description && <td style={{ padding: "8px" }}>Beispielprodukt 2</td>}
                  {cols.productType && <td style={{ padding: "8px" }}>Ware</td>}
                  {cols.itemDate && <td style={{ padding: "8px" }}>27.12.2024</td>}
                  {cols.quantity && <td style={{ textAlign: "right", padding: "8px" }}>1</td>}
                  {cols.unit && <td style={{ textAlign: "right", padding: "8px" }}>Stk</td>}
                  {cols.unitPrice && <td style={{ textAlign: "right", padding: "8px" }}>{formatCurrency(75)}</td>}
                  {cols.total && <td style={{ textAlign: "right", padding: "8px" }}>{formatCurrency(75)}</td>}
                </tr>
              </tbody>
            </table>
            {/* Totals section */}
            <div style={{ marginTop: "16px", display: "flex", justifyContent: "flex-end" }}>
              <div style={{ minWidth: "250px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: `1px solid ${tableAccentColor}40` }}>
                  <span>Gesamt netto:</span>
                  <span style={{ fontWeight: "500" }}>{formatCurrency(175)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: `1px solid ${tableAccentColor}40` }}>
                  <span>Umsatzsteuer (19%):</span>
                  <span style={{ fontWeight: "500" }}>{formatCurrency(33.25)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderTop: `2px solid ${tableAccentColor}`, fontWeight: "bold", fontSize: "1.1em", color: tableAccentColor }}>
                  <span>Gesamtsumme:</span>
                  <span>{formatCurrency(208.25)}</span>
                </div>
              </div>
            </div>
          </div>
        );
      }

      case "total-net":
        return "175,00 €";

      case "total-vat":
        return "33,25 €";

      case "total-gross":
        return "208,25 €";

      case "invoice-info-block": {
        const rows = el.rows || {};
        const labels = el.labels || {};
        const labelWidth = el.labelWidth || 150;
        const gap = el.gap || 1;
        
        // Get page number value based on format
        const pageFormat = el.pageNumberFormat || "page-x";
        let pageValue = "1";
        switch (pageFormat) {
          case "page-x":
            pageValue = "1";
            break;
          case "page-x-of-y":
            pageValue = "1 von 2";
            break;
          case "x":
            pageValue = "1";
            break;
          case "x-slash-y":
            pageValue = "1/2";
            break;
          case "x-dash-y":
            pageValue = "1-2";
            break;
        }
        
        // Define all possible rows with their values
        const rowDefinitions = [
          { key: 'invoiceNumber', defaultLabel: 'Rechnungsnummer', value: DUMMY_DATA.invoice.number },
          { key: 'invoiceDate', defaultLabel: 'Rechnungsdatum', value: DUMMY_DATA.invoice.date },
          { key: 'paymentDueDate', defaultLabel: 'Zahlungsbedingungen', value: DUMMY_DATA.invoice.dueDate },
          { key: 'vatId', defaultLabel: 'Ust.-IdNr.', value: actualCompanyData.vatId },
          { key: 'customerNumber', defaultLabel: 'Kundennummer', value: DUMMY_DATA.invoice.customerNumber },
          { key: 'pageNumber', defaultLabel: 'Seite', value: pageValue },
        ];
        
        // Filter visible rows
        const visibleRows = rowDefinitions.filter(row => rows[row.key] !== false);
        
        if (visibleRows.length === 0) {
          return <div style={{ color: '#999', fontSize: '0.9em' }}>Keine sichtbaren Zeilen</div>;
        }
        
        return (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.key}>
                  <td 
                    style={{ 
                      width: `${labelWidth}px`,
                      padding: `${gap * 2}px ${gap * 4}px`,
                      fontWeight: 500,
                    }}
                  >
                    {labels[row.key] || row.defaultLabel}:
                  </td>
                  <td 
                    style={{ 
                      padding: `${gap * 2}px ${gap * 4}px`,
                    }}
                  >
                    {row.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      }

      default:
        // For unknown types, try to render content if available
        return el.content || "";
    }
  };

  if (!element.visible) {
    return null;
  }

  return <div style={style}>{renderContent()}</div>;
}
